import { Box, Flex, Heading, Icon, Image, Text, useBreakpoint } from "@highoutput/ui-components";
import type { FC } from "react";
import { InView } from "react-intersection-observer";
import Navbar from "~/components/navbar";
import Footer from "../landing/v2/footer";
import For from "../utilities/for";
import { CheckIcon } from "./icon";
import type { COEProps } from "./mockData";
import { COEMockData } from "./mockData";

export const Break = () => {
  const breakpoint = useBreakpoint();
  return <br style={{ display: breakpoint === "base" ? "none" : "block" }} />;
};

const CodeOfEthics: FC = () => {
  return (
    <Flex minHeight="100vh" direction="column">
      <Navbar />
      <InView triggerOnce>
        {({ ref, inView }) => (
          <>
            <Box
              ref={ref}
              textAlign="center"
              pb={{ lg: "60px" }}
              maxWidth={{
                base: "375px",
                lg: "1024px",
                xl: "1120px",
              }}
              mx={{ base: "1rem", lg: "auto" }}
              sx={{
                opacity: 0,
                willChange: "opacity",
                transition: "opacity 350ms ease-out",
                ...(inView && {
                  opacity: 1,
                }),
              }}
            >
              <Heading
                variant={{ base: "40/44", lg: "56/64" }}
                fontWeight={400}
                color="v2.blue.100"
                my={{ base: "48px", lg: "48px" }}
              >
                Code of Ethics
              </Heading>

              <Heading
                variant={{ lg: "40/44" }}
                fontWeight={500}
                color="v2.blue.100"
                fontFamily="Inter"
                mb="20px"
              >
                Introduction
              </Heading>

              <Text
                variant={{ base: "18/28", lg: "20/32" }}
                w={{ base: "100%", lg: 960 }}
                color="rgba(13,48,80,0.8)"
                letterSpacing={{ base: "0.02em", lg: 0 }}
                fontFamily="'Inter', sans-serif"
              >
                Our company is committed to the highest ethical standards in all of our business
                practices. We have
                <Break /> developed a code of ethics to guide the actions of our employees and to
                ensure that we are
                <Break /> conducting ourselves in a responsible and legal manner. This code of
                ethics outlines our commitment
                <Break /> to honesty, integrity, and fairness in all of our business dealings. It
                also sets out the expectations we
                <Break /> have for the behavior of our employees, and the consequences for any
                violations of these
                <Break /> expectations. We believe that adhering to strong ethical principles is
                essential to the long-term
                <Break /> success of our company and the trust of our stakeholders.
              </Text>
            </Box>

            <Box
              margin="auto"
              position="relative"
              ref={ref}
              sx={{
                opacity: 0,
                willChange: "opacity",
                transition: "opacity 350ms ease-out",
                ...(inView && {
                  opacity: 1,
                }),
              }}
            >
              <Flex direction={{ base: "column", lg: "row" }} overflow="hidden">
                <Heading
                  variant={{ lg: "40/44" }}
                  fontWeight={500}
                  color="rgb(13,48,80)"
                  fontFamily="Inter"
                  justifyContent="center"
                  mt={{ base: "64px", lg: 0 }}
                  mb={{ base: "48px", lg: "1rem", xl: "80px" }}
                >
                  Our Code of Ethics
                </Heading>
              </Flex>
            </Box>
          </>
        )}
      </InView>

      <Box m={{ xl: "auto", lg: 0 }}>
        <For each={COEMockData}>
          {(ethics, index) => <Items data={ethics} reversed={(index + 1) % 2 !== 0} />}
        </For>
      </Box>

      <Footer cta />
    </Flex>
  );
};
export default CodeOfEthics;

type ItemProps = {
  reversed?: boolean;
  data: COEProps;
};

const Items: FC<ItemProps> = ({ data, reversed }) => {
  const breakpoint = useBreakpoint();
  const shouldReverse = !["base", "xs", "sm", "md", "lg"].includes(breakpoint);

  return (
    <InView triggerOnce delay={200}>
      {({ ref, inView }) => (
        <Flex
          ref={ref}
          direction={{
            base: "column",
            xl: "row",
          }}
          alignItems="center"
          mb={{ base: "40px", lg: 0, xl: "80px" }}
          justifyContent="center"
          overflow="hidden"
          px={{ base: "1rem", lg: 0 }}
          sx={{
            opacity: 0,
            transform: "translateY(10vh)",
            transition: "transform 600ms ease-out, opacity 600ms ease-out",
            willChange: "transform, opacity",
            ...(inView && {
              opacity: 1,
              transform: "none",
            }),
          }}
        >
          <Box
            w={{ lg: "auto", xl: 620, "2xl": 720 }}
            px={{ lg: "24px", xl: "96px" }}
            order={!shouldReverse ? 0 /* places text below */ : reversed ? 0 : 1}
          >
            <Box mt={{ base: "0", lg: "3rem", xl: 0 }} mb={{ base: "24px", lg: 0 }}>
              <Icon as={data.icon} width="48px" height="48px" />
            </Box>
            <Heading
              variant={{ base: "24/28", lg: "32/36" }}
              color="#0D3050"
              mt={{ lg: "24px" }}
              mb={{ lg: "2rem" }}
              fontWeight={{ base: 700, lg: 500 }}
              fontFamily="Inter"
            >
              {data.heading}
            </Heading>
            <Flex direction="column">
              <For each={data.content}>
                {(context, index) => (
                  <>
                    <Flex key={index} mb={{ lg: "20px" }} mt={{ base: "24px", lg: 0 }}>
                      <Box mt="2px" mx={{ base: "1rem", lg: 0 }}>
                        <Icon as={CheckIcon} width="14px" height="12px" />
                      </Box>
                      <Text
                        ml={{ base: 0, lg: "18px" }}
                        variant={"18/28"}
                        color="rgba(13, 48, 80, 0.8);"
                        fontFamily="'Inter', sans-serif"
                      >
                        {context}
                      </Text>
                    </Flex>
                  </>
                )}
              </For>
            </Flex>
          </Box>
          <Box
            w={{ lg: "auto", xl: 720 }}
            h={{ base: 240, lg: 420, xl: 700 }}
            mt={{ base: "40px", lg: "48px", xl: 0 }}
          >
            {shouldReverse && (
              <Image
                ref={ref}
                src={`images/ethics/${data?.heading.toLowerCase()}.webp`}
                alt=""
                loading="lazy"
                objectFit="cover"
                w="100%"
                h={700}
              />
            )}

            {breakpoint === "lg" && (
              <Image
                ref={ref}
                src={`images/ethics/${data?.heading.toLowerCase()}-2.webp`}
                alt=""
                loading="lazy"
                objectFit="cover"
                w="100%"
                h={415}
              />
            )}

            {breakpoint === "base" && (
              <Image
                ref={ref}
                src={`images/ethics/${data?.heading.toLowerCase()}-3.webp`}
                alt=""
                loading="lazy"
                objectFit="cover"
                w="100%"
                h={240}
              />
            )}
          </Box>
        </Flex>
      )}
    </InView>
  );
};
